import { useState } from 'react';
import { useAppSettings } from '../hooks/useAppSettings';
import { useAuthHeaders } from './useAuthHeaders';

interface DownloadFileParams {
    category: string;
    fileName: string;
}

export const useDownloadFile = () => {
    const { settings: appSettings } = useAppSettings();
    const { getAuthHeaders } = useAuthHeaders();
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const downloadFile = async ({ category, fileName }: DownloadFileParams): Promise<void> => {
        if (!appSettings?.api) {
            throw new Error('API settings not available');
        }

        setIsDownloading(true);
        setDownloadError(null);

        try {
            const response = await fetch(
                `${appSettings.api}/DownloadFile?category=${encodeURIComponent(category)}&fileName=${encodeURIComponent(fileName)}`,
                {
                    method: 'GET',
                    headers: getAuthHeaders(),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Download failed (${response.status}): ${errorText || response.statusText}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            setDownloadError(error.message || 'Failed to download file');
            throw error;
        } finally {
            setIsDownloading(false);
        }
    };

    const getFileUrl = (category: string, fileName: string): string => {
        if (!appSettings?.api || !fileName) return '';
        return `${appSettings.api}/DownloadFile?category=${encodeURIComponent(category)}&fileName=${encodeURIComponent(fileName)}`;
    };

    return {
        downloadFile,
        getFileUrl,
        isDownloading,
        downloadError,
    };
};
